/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  Autocomplete component
 */

import type { BoxProps } from '@chakra-ui/react'
import type { AutocompleteApiResult, AutocompleteCurrentPositionResult } from '@zcode/bizos'
import { createAllowedPropsAreEqual } from '@zcode/react-utils'
import { type FormikProps, withFormik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import type { Shared } from 'react-redux'
import type * as Api from '../../api'
import { useGeoLocation } from '../GeoLocation'
import { selectLat, selectLng } from '../GeoLocation/selectors'
import { AutocompleteContext } from './AutocompleteContext'
import { AutocompleteFormContainer } from './AutocompleteForm/AutocompleteFormContainer'
import { AutocompleteContextualMenu } from './AutocompleteResults/AutocompleteContextualMenu'
import type { AutocompleteOrigin, AutocompleteTheme } from './constants'
import { trackExitClick } from './module/thunks/analytics'
import type { PageNameValues } from './trackSearchBarClick'

export interface SharedAutocompleteProps {
  ariaDescribedby?: string
  children?: React.ReactNode
  exclude?: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  fillInputOnSelect?: boolean
  forceContextualMenuOpen?: boolean
  ghostTextProps?: BoxProps
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  hasError?: boolean
  hideMobileAutocomplete?: () => void
  inputValueProps?: BoxProps
  isMapListNav?: boolean
  isMobile?: boolean
  isShortTerm?: boolean
  dispatchMixpanelAutocompleteResultClick?: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  handleSearchButtonClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
    hoveredIndex: number,
    searchTerm: string
  ) => void
  onError?: (error: { message: string }) => void
  onSelectAutocompleteResult: (
    result: AutocompleteApiResult | AutocompleteCurrentPositionResult
  ) => Promise<void> | void
  origin?: AutocompleteOrigin
  pageName?: PageNameValues
  placeholder?: string
  resetForm: () => void
  setFieldValue: (field: 'searchTerm', value: string, shouldValidate?: boolean | undefined) => void // from formik
  shouldFocusAutoComplete?: boolean
  showMobileAutocomplete?: boolean
  theme: AutocompleteTheme
  values: { searchTerm: string } // from formik
}

const SharedAutocompleteComponent = ({
  ariaDescribedby,
  children,
  exclude,
  ghostTextProps,
  handleChange,
  hasError,
  hideMobileAutocomplete,
  inputValueProps,
  isMapListNav,
  isMobile,
  isShortTerm,
  fillInputOnSelect,
  dispatchMixpanelAutocompleteResultClick,
  handleSearchButtonClick,
  onError,
  onSelectAutocompleteResult,
  forceContextualMenuOpen: forceContextualMenuOpenProps,
  origin,
  pageName,
  placeholder,
  resetForm,
  shouldFocusAutoComplete,
  showMobileAutocomplete,
  setFieldValue,
  theme,
  values,
}: SharedAutocompleteProps) => {
  // Context Values
  const [forceContextualMenuOpen, setForceContextualMenuOpen] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(-1)
  const [isFocused, setIsFocused] = useState(false)
  const [keystrokes, setKeystrokes] = useState(0)
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [predictionResults, setPredictionResults] = useState<
    readonly AutocompleteApiResult[] | null
  >(null)
  const [prevSearchTerm, setPrevSearchTerm] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const store = {
    forceContextualMenuOpen,
    hoveredIndex,
    isFocused,
    keystrokes,
    lat,
    lng,
    predictionResults,
    prevSearchTerm,
    searchTerm,
    setForceContextualMenuOpen,
    setHoveredIndex,
    setIsFocused,
    setKeystrokes,
    setLat,
    setLng,
    setPredictionResults,
    setPrevSearchTerm,
    setSearchTerm,
  }

  const onClickExit = () =>
    trackExitClick({ origin, keystrokes, predictionResults, searchTerm, setKeystrokes })

  const [getGeoLocation, isSafeForClientOnly] = useGeoLocation()
  const geoLocation = isSafeForClientOnly ? getGeoLocation() : {}
  const geoLat = selectLat(geoLocation)
  const geoLng = selectLng(geoLocation)
  useEffect(() => {
    if (lat !== geoLat) {
      geoLat && setLat(geoLat)
    }
    if (lng !== geoLng) {
      geoLng && setLng(geoLng)
    }
  }, [lat, lng, setLat, setLng, geoLat, geoLng])

  useEffect(() => {
    const checkExitClick = (e: MouseEvent) => {
      const ref = autocompleteRef?.current
      const target = e?.target
      if (ref && target) {
        const isExitClick = !(e.target instanceof Node) || !ref.contains(e.target)
        if (isExitClick) {
          onClickExit?.()
        }
      }
    }
    if (isFocused) {
      window.document.addEventListener('mousedown', checkExitClick)
    }
    return () => {
      window.document.removeEventListener('mousedown', checkExitClick)
    }
  }, [isFocused, onClickExit])

  const autocompleteRef = useRef<HTMLDivElement | null>(null)
  const { searchTerm: formSearchTerm } = values
  const distanceFromTop = autocompleteRef?.current?.getBoundingClientRect().top

  return (
    <AutocompleteContext.Provider value={store}>
      <div className={theme.container} ref={autocompleteRef}>
        {!isMobile && (
          <AutocompleteFormContainer
            ariaDescribedby={ariaDescribedby}
            exclude={exclude}
            fillInputOnSelect={fillInputOnSelect}
            ghostTextProps={ghostTextProps}
            handleChange={handleChange}
            handleSearchButtonClick={handleSearchButtonClick}
            hasError={hasError}
            inputValueProps={inputValueProps}
            isShortTerm={isShortTerm}
            onError={onError}
            onSelectAutocompleteResult={onSelectAutocompleteResult}
            origin={origin}
            pageName={pageName}
            placeholder={placeholder}
            resetForm={resetForm}
            searchTerm={formSearchTerm}
            setFieldValue={setFieldValue}
            shouldFocusAutoComplete={shouldFocusAutoComplete}
            theme={theme}
          />
        )}
        <AutocompleteContextualMenu
          children={children}
          dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
          distanceFromTop={distanceFromTop}
          exclude={exclude}
          fillInputOnSelect={fillInputOnSelect}
          forceContextualMenuOpen={forceContextualMenuOpenProps}
          handleChange={handleChange}
          hideMobileAutocomplete={hideMobileAutocomplete}
          isMapListNav={isMapListNav}
          isShortTerm={isShortTerm}
          onError={onError}
          onSelectAutocompleteResult={onSelectAutocompleteResult}
          origin={origin}
          placeholder={placeholder}
          resetForm={resetForm}
          searchTerm={formSearchTerm}
          setFieldValue={setFieldValue}
          showMobileAutocomplete={showMobileAutocomplete}
          theme={theme}
        />
      </div>
    </AutocompleteContext.Provider>
  )
}

const equalProps = [
  'children',
  'exclude',
  'forceContextualMenuOpen',
  'handleChange',
  'hideMobileAutocomplete',
  'isMobile',
  'fillInputOnSelect',
  'onError',
  'onSelectAutocompleteResult',
  'origin',
  'placeholder',
  'resetForm',
  'shouldFocusAutoComplete',
  'showMobileAutocomplete',
] as const

const shallowEqualProps = ['theme', 'values'] as const

const formik = withFormik<
  Omit<
    SharedAutocompleteProps,
    keyof Shared<FormikProps<{ searchTerm: string }>, SharedAutocompleteProps>
  >,
  { searchTerm: string }
  // @ts-expect-error
>({
  mapPropsToValues: () => {
    return {
      searchTerm: '',
    }
  },
})

export const MemoSharedAutocomplete = React.memo(
  SharedAutocompleteComponent,
  createAllowedPropsAreEqual(equalProps, shallowEqualProps)
)

export const SharedAutocomplete = formik(MemoSharedAutocomplete)
